import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { 
  Car, 
  Users, 
  DollarSign, 
  AlertTriangle, 
  Wrench,
  TrendingUp,
  Calendar,
  Clock,
  Eye,
  Info,
  ChevronRight,
  Bike,
  CircleDollarSign,
  UserCheck,
  AlertCircle,
  Coins,
  Bell,
  BellOff,
  Settings,
  RefreshCw,
  CheckCircle
} from 'lucide-react';
import { apiService } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [resumo, setResumo] = useState(null);
  const [alertas, setAlertas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDateTime, setCurrentDateTime] = useState(new Date());

  const isSubAdmin = user?.tipo === 'sub_admin';

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    carregarDados();
  }, []);

  const formatarDataHoraBrasilia = (data) => {
    try {
      const options = {
        timeZone: 'America/Sao_Paulo',
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      };
      return data.toLocaleString('pt-BR', options);
    } catch {
      return data.toLocaleDateString('pt-BR') + ' ' + data.toLocaleTimeString('pt-BR');
    }
  };

  const carregarDados = async () => {
    try {
      try {
        const resumoData = await apiService.getDashboardResumo();
        setResumo(resumoData);
      } catch (error) {
        console.error('Erro ao carregar resumo:', error);
      }
      try {
        const alertasData = await apiService.getAlertasUrgentes();
        setAlertas(alertasData);
      } catch {
        setAlertas([]);
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-500">Carregando dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-1" data-testid="dashboard-page">
      {/* Alerta Sub-Admin */}
      {isSubAdmin && (
        <Alert className="bg-blue-50 border-blue-200 rounded-xl">
          <Info className="h-5 w-5 text-gray-400" />
          <AlertDescription className="text-blue-800">
            <strong>Modo Somente Leitura</strong> - Você está visualizando dados compartilhados.
          </AlertDescription>
        </Alert>
      )}
      
      {/* Header com Data/Hora — visual mais discreto e harmonioso */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-emerald-900/40 via-emerald-800/30 to-emerald-900/40 border border-emerald-500/15 px-4 py-3">
        <div className="absolute -top-6 -right-6 w-32 h-32 bg-emerald-400/5 rounded-full blur-2xl pointer-events-none"></div>
        <div className="relative flex items-center gap-3">
          <div className="p-2 bg-emerald-500/15 rounded-lg border border-emerald-400/20">
            <Clock className="h-4 w-4 text-emerald-300" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-emerald-300/70 text-[10px] font-medium uppercase tracking-widest">Horário de Brasília</p>
            <p className="text-sm md:text-base font-semibold text-white capitalize truncate tabular-nums">
              {formatarDataHoraBrasilia(currentDateTime)}
            </p>
          </div>
        </div>
      </div>

      {/* Cards Principais - Design Premium com Ilustrações Sutis */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total de Motos */}
        <div 
          className="group relative cursor-pointer overflow-hidden rounded-2xl h-36 transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl hover:shadow-blue-500/20"
          onClick={() => navigate('/admin/motos')}
        >
          {/* Fundo com padrão de motos */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-blue-800" />
          <div className="absolute inset-0 opacity-10">
            <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
              <pattern id="motoPattern" x="0" y="0" width="25" height="25" patternUnits="userSpaceOnUse">
                <path d="M12 8c-2 0-3.5 1.5-3.5 3.5s1.5 3.5 3.5 3.5 3.5-1.5 3.5-3.5S14 8 12 8zm8 3.5c0-1-.8-1.8-1.8-1.8H16v3.5h2.2c1 0 1.8-.7 1.8-1.7zM5 11.5c0-1 .8-1.8 1.8-1.8H9v3.5H6.8c-1 0-1.8-.7-1.8-1.7z" fill="white"/>
              </pattern>
              <rect width="100" height="100" fill="url(#motoPattern)"/>
            </svg>
          </div>
          {/* Ícone decorativo grande no fundo */}
          <div className="absolute -right-4 -bottom-4 opacity-20 group-hover:opacity-30 transition-opacity">
            <Car className="h-32 w-32 text-white" />
          </div>
          <div className="absolute inset-0 p-5 flex flex-col justify-between">
            <div className="flex items-center justify-between">
              <div className="p-2.5 bg-white/20 backdrop-blur-sm rounded-xl group-hover:scale-110 transition-transform">
                <Car className="h-5 w-5 text-white" />
              </div>
              <ChevronRight className="h-5 w-5 text-white/60 group-hover:translate-x-1 group-hover:text-white transition-all" />
            </div>
            <div>
              <p className="text-3xl font-bold text-white">{resumo?.total_motos || 0}</p>
              <p className="text-sm text-blue-200 font-medium">Total de Motos</p>
            </div>
          </div>
        </div>

        {/* Motos Alugadas */}
        <div 
          className="group relative cursor-pointer overflow-hidden rounded-2xl h-36 transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl hover:shadow-green-500/20"
          onClick={() => navigate('/admin/motos-alugadas')}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-green-600 to-emerald-800" />
          {/* Ícone decorativo grande no fundo */}
          <div className="absolute -right-4 -bottom-4 opacity-20 group-hover:opacity-30 transition-opacity">
            <Bike className="h-32 w-32 text-white" />
          </div>
          <div className="absolute inset-0 p-5 flex flex-col justify-between">
            <div className="flex items-center justify-between">
              <div className="p-2.5 bg-white/20 backdrop-blur-sm rounded-xl group-hover:scale-110 transition-transform">
                <Bike className="h-5 w-5 text-white" />
              </div>
              <ChevronRight className="h-5 w-5 text-white/60 group-hover:translate-x-1 group-hover:text-white transition-all" />
            </div>
            <div>
              <p className="text-3xl font-bold text-white">{resumo?.motos_alugadas || 0}</p>
              <p className="text-sm text-green-200 font-medium">Motos Alugadas</p>
            </div>
          </div>
        </div>

        {/* Clientes Ativos */}
        <div 
          className="group relative cursor-pointer overflow-hidden rounded-2xl h-36 transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl hover:shadow-purple-500/20"
          onClick={() => navigate('/admin/clientes')}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-purple-600 to-violet-800" />
          {/* Padrão de pessoas no fundo */}
          <div className="absolute inset-0 opacity-15">
            <div className="absolute top-2 left-2"><Users className="h-8 w-8 text-white" /></div>
            <div className="absolute top-4 right-8"><Users className="h-6 w-6 text-white" /></div>
            <div className="absolute bottom-8 left-8"><Users className="h-7 w-7 text-white" /></div>
          </div>
          {/* Ícone decorativo grande no fundo */}
          <div className="absolute -right-4 -bottom-4 opacity-20 group-hover:opacity-30 transition-opacity">
            <UserCheck className="h-32 w-32 text-white" />
          </div>
          <div className="absolute inset-0 p-5 flex flex-col justify-between">
            <div className="flex items-center justify-between">
              <div className="p-2.5 bg-white/20 backdrop-blur-sm rounded-xl group-hover:scale-110 transition-transform">
                <UserCheck className="h-5 w-5 text-white" />
              </div>
              <ChevronRight className="h-5 w-5 text-white/60 group-hover:translate-x-1 group-hover:text-white transition-all" />
            </div>
            <div>
              <p className="text-3xl font-bold text-white">{resumo?.total_clientes || 0}</p>
              <p className="text-sm text-purple-200 font-medium">Clientes Ativos</p>
            </div>
          </div>
        </div>

        {/* Receita Mensal */}
        <div 
          className="group relative cursor-pointer overflow-hidden rounded-2xl h-36 transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl hover:shadow-amber-500/20"
          onClick={() => navigate('/admin/receitas')}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-amber-500 to-orange-700" />
          {/* Padrão de moedas/dinheiro no fundo */}
          <div className="absolute inset-0 opacity-15">
            <div className="absolute top-3 left-3"><Coins className="h-6 w-6 text-white" /></div>
            <div className="absolute top-2 right-12"><Coins className="h-5 w-5 text-white" /></div>
            <div className="absolute bottom-10 left-10"><Coins className="h-7 w-7 text-white" /></div>
          </div>
          {/* Ícone decorativo grande no fundo */}
          <div className="absolute -right-4 -bottom-4 opacity-20 group-hover:opacity-30 transition-opacity">
            <DollarSign className="h-32 w-32 text-white" />
          </div>
          <div className="absolute inset-0 p-5 flex flex-col justify-between">
            <div className="flex items-center justify-between">
              <div className="p-2.5 bg-white/20 backdrop-blur-sm rounded-xl group-hover:scale-110 transition-transform">
                <DollarSign className="h-5 w-5 text-white" />
              </div>
              <ChevronRight className="h-5 w-5 text-white/60 group-hover:translate-x-1 group-hover:text-white transition-all" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">R$ {(resumo?.receita_mensal || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
              <p className="text-sm text-amber-200 font-medium">Receita Mensal</p>
            </div>
          </div>
        </div>
      </div>

      {/* Status Rápido - Cards Modernos */}
      <div className="grid grid-cols-3 gap-4">
        {/* Disponíveis */}
        <div className="group relative overflow-hidden rounded-2xl h-28 bg-gradient-to-br from-emerald-500 to-teal-600 shadow-lg hover:shadow-xl hover:shadow-emerald-500/20 transition-all cursor-pointer"
             onClick={() => navigate('/admin/motos')}>
          <div className="absolute -right-3 -bottom-3 opacity-20 group-hover:opacity-30 transition-opacity">
            <Car className="h-24 w-24 text-white" />
          </div>
          <div className="absolute inset-0 p-4 flex flex-col justify-between">
            <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg w-fit">
              <Car className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="text-3xl font-bold text-white">{resumo?.motos_disponiveis || 0}</p>
              <p className="text-sm text-emerald-100">Disponíveis</p>
            </div>
          </div>
        </div>
        
        {/* Em Manutenção */}
        <div className="group relative overflow-hidden rounded-2xl h-28 bg-gradient-to-br from-orange-500 to-red-500 shadow-lg hover:shadow-xl hover:shadow-orange-500/20 transition-all cursor-pointer"
             onClick={() => navigate('/admin/manutencao')}>
          <div className="absolute -right-3 -bottom-3 opacity-20 group-hover:opacity-30 transition-opacity">
            <Wrench className="h-24 w-24 text-white" />
          </div>
          <div className="absolute inset-0 p-4 flex flex-col justify-between">
            <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg w-fit">
              <Wrench className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="text-3xl font-bold text-white">{resumo?.motos_manutencao || 0}</p>
              <p className="text-sm text-orange-100">Em Manutenção</p>
            </div>
          </div>
        </div>
        
        {/* Locações Ativas */}
        <div className="group relative overflow-hidden rounded-2xl h-28 bg-gradient-to-br from-indigo-500 to-purple-600 shadow-lg hover:shadow-xl hover:shadow-indigo-500/20 transition-all cursor-pointer"
             onClick={() => navigate('/admin/locacoes')}>
          <div className="absolute -right-3 -bottom-3 opacity-20 group-hover:opacity-30 transition-opacity">
            <TrendingUp className="h-24 w-24 text-white" />
          </div>
          <div className="absolute inset-0 p-4 flex flex-col justify-between">
            <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg w-fit">
              <TrendingUp className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="text-3xl font-bold text-white">{resumo?.locacoes_ativas || 0}</p>
              <p className="text-sm text-indigo-100">Locações Ativas</p>
            </div>
          </div>
        </div>
      </div>

      {/* Alertas e Pendências - Cards Modernos */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Manutenções */}
        <div 
          className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 shadow-lg hover:shadow-xl hover:shadow-amber-500/20 transition-all cursor-pointer"
          onClick={() => navigate('/admin/brasilsat')}
        >
          <div className="absolute -right-4 -bottom-4 opacity-15 group-hover:opacity-25 transition-opacity">
            <Wrench className="h-28 w-28 text-white" />
          </div>
          <div className="p-5">
            <div className="flex items-center gap-4 mb-3">
              <div className="p-2.5 bg-white/20 backdrop-blur-sm rounded-xl">
                <Wrench className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-100">Manutenções</p>
                <p className="text-2xl font-bold text-white">{resumo?.manutencoes_pendentes || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-black/10 px-5 py-2">
            <p className="text-xs text-white/80 font-medium">Motos precisam de revisão</p>
          </div>
        </div>

        {/* Pagamentos Atrasados */}
        <div 
          className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-rose-500 to-red-600 shadow-lg hover:shadow-xl hover:shadow-rose-500/20 transition-all cursor-pointer"
          onClick={() => navigate('/admin/pagamentos')}
        >
          <div className="absolute -right-4 -bottom-4 opacity-15 group-hover:opacity-25 transition-opacity">
            <AlertCircle className="h-28 w-28 text-white" />
          </div>
          <div className="p-5">
            <div className="flex items-center gap-4 mb-3">
              <div className="p-2.5 bg-white/20 backdrop-blur-sm rounded-xl">
                <AlertCircle className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-rose-100">Pagamentos</p>
                <p className="text-2xl font-bold text-white">{resumo?.pagamentos_atrasados || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-black/10 px-5 py-2">
            <p className="text-xs text-white/80 font-medium">Pagamentos em atraso</p>
          </div>
        </div>

        {/* Débitos Pendentes */}
        <div 
          className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-yellow-500 to-amber-600 shadow-lg hover:shadow-xl hover:shadow-yellow-500/20 transition-all cursor-pointer"
          onClick={() => navigate('/admin/cobrancas')}
        >
          <div className="absolute -right-4 -bottom-4 opacity-15 group-hover:opacity-25 transition-opacity">
            <AlertTriangle className="h-28 w-28 text-white" />
          </div>
          <div className="p-5">
            <div className="flex items-center gap-4 mb-3">
              <div className="p-2.5 bg-white/20 backdrop-blur-sm rounded-xl">
                <AlertTriangle className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-yellow-100">Débitos</p>
                <p className="text-2xl font-bold text-white">{resumo?.cobrancas_extras_pendentes || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-black/10 px-5 py-2">
            <p className="text-xs text-white/80 font-medium">Cobranças extras pendentes</p>
          </div>
        </div>
      </div>
    </div>
  );
};
